import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Ban, ChevronDown, ChevronRight, ListTodo, Plus, RotateCcw, Trash2, X } from 'lucide-react';
import { Role } from '@jeket-cloud/types/auth';
import { RoleGate } from '@/components/auth/RoleGate';
import { cn } from '@/lib/utils';
import { useCreateJob, useJobActions, useJobs, useRecordings } from '@/hooks/useProcessing';
import type { JobFeature, JobStatus, ProcessingJob, Recording } from '@/types/processing';
import {
  Card,
  EmptyState,
  ErrorState,
  FEATURE_META,
  FeatureBadge,
  InlineError,
  LoadingState,
  PageHeader,
  ProgressBar,
  StatusBadge,
  TRIGGER_LABEL,
  buttonPrimary,
  formatDateTime,
  formatDuration,
  formatWallclock,
  iconButton,
  inputClass,
} from './shared';

const STATUS_FILTERS: (JobStatus | 'all')[] = ['all', 'pending', 'running', 'completed', 'failed', 'cancelled', 'skipped'];
const FEATURE_FILTERS: (JobFeature | 'all')[] = ['all', 'clip_cut', 'social_format', 'thumbnails', 'final_summary'];
/** Whole-recording features a user can queue by hand; clips are cut on the timeline. */
const MANUAL_FEATURES: JobFeature[] = ['final_summary', 'thumbnails'];
const PAGE = 100;

const CANCELLABLE: JobStatus[] = ['pending', 'running'];
const RESTARTABLE: JobStatus[] = ['failed', 'cancelled', 'completed', 'skipped'];

function JobDetail({ job, recording }: { job: ProcessingJob; recording?: Recording }) {
  const rows: [string, React.ReactNode][] = [
    ['Stream', job.streamId],
    ['Trigger', TRIGGER_LABEL[job.trigger] ?? job.trigger],
    ['Range', job.start != null && job.end != null ? `${formatWallclock(job.start)} · ${formatDuration(job.end - job.start)}` : 'Whole recording'],
    ['Attempts', job.attempts],
    ['Updated', formatDateTime(job.updatedAt)],
  ];
  return (
    <div className="px-4 pb-4 sm:pl-14">
      <div className="p-3 bg-jeket-black-deep rounded-lg border border-jeket-gold/10 text-xs space-y-2">
        <dl className="grid grid-cols-[6rem_1fr] gap-x-3 gap-y-1">
          {rows.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-jeket-silver-muted">{k}</dt>
              <dd className="text-jeket-silver-bright break-all">{v}</dd>
            </div>
          ))}
        </dl>
        {job.error && <p className="text-jeket-error break-words">{job.error}</p>}
        {job.params && Object.keys(job.params).length > 0 && (
          <pre className="text-jeket-silver-muted whitespace-pre-wrap break-all">params: {JSON.stringify(job.params, null, 2)}</pre>
        )}
        {job.result && (
          <pre className="text-jeket-silver-muted whitespace-pre-wrap break-all">result: {JSON.stringify(job.result, null, 2)}</pre>
        )}
        {recording && (
          <Link to={`/media/recordings/${encodeURIComponent(recording.sourceId)}`} className="inline-block text-jeket-gold hover:underline">
            Open recording
          </Link>
        )}
      </div>
    </div>
  );
}

function JobRow({ job, recording }: { job: ProcessingJob; recording?: Recording }) {
  const [expanded, setExpanded] = useState(false);
  const { cancel, restart, remove } = useJobActions();
  const actionError = cancel.error ?? restart.error ?? remove.error;

  return (
    <li className="border-b border-jeket-gold/10 last:border-b-0" data-testid={`job-row-${job.id}`}>
      <div
        className="flex flex-wrap md:flex-nowrap items-center gap-x-4 gap-y-2 px-4 py-3 hover:bg-jeket-black-elevated/50 cursor-pointer transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="w-5 hidden sm:block">
          {expanded ? <ChevronDown size={14} className="text-jeket-silver-muted" /> : <ChevronRight size={14} className="text-jeket-silver-muted" />}
        </div>
        <div className="w-full md:w-48 min-w-0">
          <p className="text-sm text-jeket-silver-bright truncate">{recording?.label || job.streamId}</p>
          <p className="text-[11px] text-jeket-silver-muted truncate">{TRIGGER_LABEL[job.trigger] ?? job.trigger}</p>
        </div>
        <div className="md:w-40"><FeatureBadge feature={job.feature} /></div>
        <div className="md:w-28"><StatusBadge status={job.status} /></div>
        <div className="w-24">{job.status === 'running' && <ProgressBar value={job.progress} />}</div>
        <div className="md:w-24 text-xs text-jeket-silver-muted tabular-nums">{formatDateTime(job.createdAt)}</div>
        <div className="flex-1 min-w-0 truncate text-xs text-jeket-error" title={job.error ?? undefined}>{job.error}</div>
        <RoleGate role={Role.SiteOperator}>
          <div className="flex items-center gap-1 ml-auto" onClick={(e) => e.stopPropagation()}>
            {CANCELLABLE.includes(job.status) && (
              <button type="button" onClick={() => cancel.mutate(job.id)} disabled={cancel.isPending} className={cn(iconButton, 'hover:text-jeket-error')} title="Cancel" aria-label="Cancel job">
                <Ban size={14} />
              </button>
            )}
            {RESTARTABLE.includes(job.status) && (
              <button type="button" onClick={() => restart.mutate(job.id)} disabled={restart.isPending} className={cn(iconButton, 'hover:text-jeket-gold')} title="Restart" aria-label="Restart job">
                <RotateCcw size={14} />
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                if (confirm('Delete this job and its output?')) remove.mutate(job.id);
              }}
              disabled={remove.isPending}
              className={cn(iconButton, 'hover:text-jeket-error')}
              title="Delete"
              aria-label="Delete job"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </RoleGate>
      </div>
      {actionError && <div className="px-4 pb-2"><InlineError error={actionError} /></div>}
      {expanded && <JobDetail job={job} recording={recording} />}
    </li>
  );
}

function NewJobModal({ recordings, onClose }: { recordings: Recording[]; onClose: () => void }) {
  const [sourceId, setSourceId] = useState('');
  const [features, setFeatures] = useState<JobFeature[]>([]);
  const createJob = useCreateJob();
  const rec = recordings.find((r) => r.sourceId === sourceId);

  const submit = async () => {
    if (!rec) return;
    try {
      for (const feature of features) {
        await createJob.mutateAsync({
          feature,
          streamId: rec.streamId,
          ...(rec.start != null && rec.end != null ? { start: rec.start, end: rec.end } : {}),
        });
      }
      onClose();
    } catch {
      // Surfaced below via createJob.error.
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-label="New processing job">
      <div className="bg-jeket-black-elevated border border-jeket-gold/20 rounded-xl shadow-jeket w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-jeket-silver-bright">New processing job</h2>
          <button type="button" onClick={onClose} className="text-jeket-silver-muted hover:text-jeket-silver-bright" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-4">
          <label className="block">
            <span className="block text-sm text-jeket-silver-muted mb-1">Recording</span>
            <select value={sourceId} onChange={(e) => setSourceId(e.target.value)} className={cn(inputClass, 'w-full')}>
              <option value="">Select a recording…</option>
              {recordings.map((r) => (
                <option key={r.sourceId} value={r.sourceId}>
                  {r.label || r.streamId} ({formatWallclock(r.start)})
                </option>
              ))}
            </select>
          </label>
          <div>
            <span className="block text-sm text-jeket-silver-muted mb-2">Features</span>
            <div className="space-y-2">
              {MANUAL_FEATURES.map((f) => {
                const meta = FEATURE_META[f];
                const Icon = meta.icon;
                const on = features.includes(f);
                return (
                  <label
                    key={f}
                    className={cn(
                      'flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-colors',
                      on ? 'border-jeket-gold/40 bg-jeket-gold/10' : 'border-jeket-gold/10 hover:border-jeket-gold/20',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => setFeatures(on ? features.filter((x) => x !== f) : [...features, f])}
                      className="accent-jeket-gold"
                    />
                    <Icon size={16} className={meta.color} />
                    <span className="text-sm text-jeket-silver-bright">{meta.label}</span>
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-jeket-silver-muted mt-2">Clips are cut from the recording timeline; social formats from Clip review.</p>
          </div>
        </div>
        <InlineError error={createJob.error} />
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-jeket-silver-muted hover:text-jeket-silver-bright rounded-lg">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={!rec || features.length === 0 || createJob.isPending} className={buttonPrimary}>
            {createJob.isPending ? 'Creating…' : `Create ${features.length || ''} job${features.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function JobsPage() {
  const [status, setStatus] = useState<JobStatus | 'all'>('all');
  const [feature, setFeature] = useState<JobFeature | 'all'>('all');
  const [limit, setLimit] = useState(PAGE);
  const [showNew, setShowNew] = useState(false);

  // Status is filtered client-side so the chip counts come from one list.
  const { data, isLoading, isError, error } = useJobs({
    ...(feature !== 'all' ? { feature } : {}),
    limit,
  });
  const { data: recordings = [] } = useRecordings();
  const byStream = useMemo(() => new Map(recordings.map((r) => [r.streamId, r])), [recordings]);

  const jobs = data?.jobs ?? [];
  const counts = jobs.reduce<Record<string, number>>((acc, j) => {
    acc[j.status] = (acc[j.status] ?? 0) + 1;
    return acc;
  }, {});
  const shown = status === 'all' ? jobs : jobs.filter((j) => j.status === status);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Processing jobs"
        subtitle="Clip cuts, social formats, thumbnails and final summaries, across every recording."
        actions={
          <RoleGate role={Role.SiteOperator}>
            <button type="button" onClick={() => setShowNew(true)} className={buttonPrimary}>
              <Plus size={16} /> New job
            </button>
          </RoleGate>
        }
      />

      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
        <div className="flex items-center gap-1 flex-wrap" role="group" aria-label="Filter by status">
          {STATUS_FILTERS.map((s) => {
            const count = s === 'all' ? jobs.length : (counts[s] ?? 0);
            return (
              <button
                key={s}
                type="button"
                aria-pressed={status === s}
                onClick={() => setStatus(s)}
                className={cn(
                  'px-3 py-1.5 text-sm rounded-lg transition-colors capitalize border',
                  status === s
                    ? 'bg-jeket-gold/20 text-jeket-gold border-jeket-gold/30'
                    : 'text-jeket-silver-muted hover:text-jeket-silver-bright hover:bg-jeket-black-elevated border-transparent',
                )}
              >
                {s === 'all' ? 'All' : s}
                {count > 0 && <span className="ml-1.5 text-xs opacity-70">({count})</span>}
              </button>
            );
          })}
        </div>
        <select
          aria-label="Filter by feature"
          value={feature}
          onChange={(e) => setFeature(e.target.value as JobFeature | 'all')}
          className={cn(inputClass, 'lg:ml-auto')}
        >
          {FEATURE_FILTERS.map((f) => (
            <option key={f} value={f}>
              {f === 'all' ? 'All features' : FEATURE_META[f].label}
            </option>
          ))}
        </select>
      </div>

      <Card className="overflow-hidden">
        <div className="hidden md:flex items-center gap-4 px-4 py-2.5 border-b border-jeket-gold/20 text-xs font-medium text-jeket-silver-muted uppercase tracking-wider">
          <div className="w-5" />
          <div className="w-48">Recording</div>
          <div className="w-40">Feature</div>
          <div className="w-28">Status</div>
          <div className="w-24">Progress</div>
          <div className="w-24">Created</div>
          <div className="flex-1">Error</div>
          <div className="w-24 text-right">Actions</div>
        </div>
        {isLoading ? (
          <LoadingState label="Loading jobs…" />
        ) : isError ? (
          <ErrorState title="Couldn't load processing jobs" error={error} />
        ) : shown.length === 0 ? (
          <EmptyState
            icon={ListTodo}
            title={jobs.length === 0 ? 'No processing jobs' : `No ${status} jobs`}
            hint="Jobs are created from highlight marks, when an event ends, or manually."
          />
        ) : (
          <ul>
            {shown.map((job) => (
              <JobRow key={job.id} job={job} recording={byStream.get(job.streamId)} />
            ))}
          </ul>
        )}
      </Card>

      {data && data.total > jobs.length && (
        <div className="flex justify-center mt-4">
          <button type="button" onClick={() => setLimit(limit + PAGE)} className="px-4 py-2 text-sm text-jeket-gold hover:underline">
            Load more ({jobs.length} of {data.total})
          </button>
        </div>
      )}

      {showNew && <NewJobModal recordings={recordings} onClose={() => setShowNew(false)} />}
    </div>
  );
}
